import { useCallback, useEffect, useState } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import './Broadcasts.css';

function Broadcasts() {
  const { hasPermission, user } = useAuth();
  const [broadcasts, setBroadcasts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [expandedBroadcastId, setExpandedBroadcastId] = useState(null);
  const [commentText, setCommentText] = useState({});
  const [adminView, setAdminView] = useState(false);
  const [allComments, setAllComments] = useState([]);
  const [commentStats, setCommentStats] = useState(null);
  const [editingBroadcast, setEditingBroadcast] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    priority: 'medium'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');

  const isAdmin = useCallback(() => {
    return user && user.role && (user.role.name === 'Admin' || user.role.name === 'HR Manager');
  }, [user]);

  const fetchBroadcasts = useCallback(async () => {
    try {
      let response;
      if (adminView && isAdmin()) {
        response = await api.get('/broadcasts/admin/all');
      } else {
        response = await api.get('/broadcasts');
      }
      setBroadcasts(response.data);

      // Mark all broadcasts as viewed (only for regular view)
      if (!adminView) {
        response.data.forEach(async (broadcast) => {
          try {
            await api.post(`/broadcasts/${broadcast._id}/view`);
          } catch (err) {
            // Silently fail
          }
        });
      }
    } catch (error) {
      console.error('Error fetching broadcasts:', error);
    }
  }, [adminView, isAdmin]);

  const fetchAllComments = useCallback(async () => {
    try {
      const response = await api.get('/broadcasts/admin/comments/all');
      setAllComments(response.data);
    } catch (error) {
      console.error('Error fetching all comments:', error);
    }
  }, []);

  const fetchCommentStats = useCallback(async () => {
    try {
      const response = await api.get('/broadcasts/admin/comments/stats');
      setCommentStats(response.data);
    } catch (error) {
      console.error('Error fetching comment stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchBroadcasts();
    if (isAdmin()) {
      fetchCommentStats();
    }
  }, [fetchBroadcasts, isAdmin, fetchCommentStats]);

  useEffect(() => {
    if (adminView && isAdmin()) {
      fetchAllComments();
    }
    fetchBroadcasts(); // Refetch broadcasts when switching views
  }, [adminView, fetchBroadcasts, isAdmin, fetchAllComments]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/broadcasts', formData);
      setShowModal(false);
      setFormData({ title: '', message: '', priority: 'medium' });
      fetchBroadcasts();
    } catch (error) {
      alert('Error creating broadcast');
    }
  };

  const handleEdit = (broadcast) => {
    setEditingBroadcast(broadcast);
    setFormData({
      title: broadcast.title,
      message: broadcast.message,
      priority: broadcast.priority
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/broadcasts/${editingBroadcast._id}`, formData);
      setShowEditModal(false);
      setEditingBroadcast(null);
      setFormData({ title: '', message: '', priority: 'medium' });
      fetchBroadcasts();
    } catch (error) {
      alert('Error updating broadcast');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this broadcast?')) {
      try {
        await api.delete(`/broadcasts/${id}`);
        fetchBroadcasts();
      } catch (error) {
        alert('Error deleting broadcast');
      }
    }
  };

  const handleAddComment = async (broadcastId) => {
    const comment = commentText[broadcastId]?.trim();
    if (!comment) {
      alert('Comment cannot be empty');
      return;
    }

    try {
      await api.post(`/broadcasts/${broadcastId}/comments`, { comment });
      setCommentText({ ...commentText, [broadcastId]: '' });
      fetchBroadcasts();
    } catch (error) {
      alert('Error adding comment');
    }
  };

  const handleDeleteComment = async (broadcastId, commentId) => {
    if (window.confirm('Delete this comment?')) {
      try {
        if (isAdmin()) {
          await api.delete(`/broadcasts/admin/comments/${commentId}`);
        } else {
          await api.delete(`/broadcasts/${broadcastId}/comments/${commentId}`);
        }
        fetchBroadcasts();
        if (adminView) {
          fetchAllComments();
        }
      } catch (error) {
        alert('Error deleting comment');
      }
    }
  };

  const toggleAdminView = () => {
    setAdminView(!adminView);
    setExpandedBroadcastId(null);
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      low: 'badge-info',
      medium: 'badge-warning',
      high: 'badge-danger'
    };
    return badges[priority] || 'badge-info';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      high: '#dc2626',
      medium: '#1d4ed8',
      low: '#166534'
    };
    return colors[priority] || '#1d4ed8';
  };

  const filteredBroadcasts = broadcasts.filter(b => {
    const matchesSearch = b.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      b.message.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPriority = priorityFilter ? b.priority === priorityFilter : true;

    let matchesDate = true;
    if (dateFilter) {
      const bDate = new Date(b.createdAt);
      const now = new Date();
      if (dateFilter === 'today') {
        matchesDate = bDate.toDateString() === now.toDateString();
      } else if (dateFilter === 'week') {
        const lastWeek = new Date(now.setDate(now.getDate() - 7));
        matchesDate = bDate >= lastWeek;
      } else if (dateFilter === 'month') {
        matchesDate = bDate.getMonth() === now.getMonth() && bDate.getFullYear() === now.getFullYear();
      }
    }

    return matchesSearch && matchesPriority && matchesDate;
  });

  return (
    <div className="broadcast-container">
      {/* Header Section */}
      <div className="broadcast-header">
        <div className="broadcast-header-top">
          <span className="broadcast-header-icon">📣</span>
          <h1>Broadcast Announcements</h1>
          {isAdmin() && (
            <button
              className={`admin-toggle-btn ${adminView ? 'active' : ''}`}
              onClick={toggleAdminView}
            >
              {adminView ? '👤 Exit Admin' : '🔧 Admin View'}
            </button>
          )}
        </div>
        <p className="broadcast-subtitle">Manage company-wide communications.</p>
      </div>

      {/* Actions Bar */}
      <div className="broadcast-actions-bar">
        <div className="search-wrapper">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            className="search-input"
            placeholder="Search announcements..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        {(hasPermission('broadcasts', 'create') || isAdmin()) && (
          <button className="btn-new-broadcast" onClick={() => setShowModal(true)}>
            <span>+</span> New Broadcast
          </button>
        )}
      </div>

      {/* Filters Bar */}
      <div className="broadcast-filters-bar">
        <select
          className="filter-select"
          value={departmentFilter}
          onChange={(e) => setDepartmentFilter(e.target.value)}
        >
          <option value="">Department ▼</option>
          <option value="HR">HR</option>
          <option value="IT">IT</option>
          <option value="Sales">Sales</option>
        </select>

        <select
          className="filter-select"
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
        >
          <option value="">Priority ▼</option>
          <option value="high">High</option>
          <option value="medium">Normal</option>
          <option value="low">Low</option>
        </select>

        <select
          className="filter-select"
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
        >
          <option value="">Date ▼</option>
          <option value="today">Today</option>
          <option value="week">Past Week</option>
          <option value="month">This Month</option>
        </select>
      </div>

      {/* Admin Stats View (Visible if adminView is on) */}
      {adminView && isAdmin() && commentStats && (
        <div className="announcement-card" style={{ background: '#f8fafc', border: '1px dashed #667eea' }}>
          <h3 style={{ fontSize: '16px', marginBottom: '15px', color: '#667eea' }}>📊 Admin Insights</h3>
          <div style={{ display: 'flex', gap: '30px', fontSize: '14px' }}>
            <span>💬 <strong>{commentStats.totalComments}</strong> Total Comments</span>
            <span>📅 <strong>{commentStats.recentCommentsCount}</strong> This Week</span>
          </div>
        </div>
      )}

      {/* Broadcast List */}
      <div className="broadcast-list">
        {filteredBroadcasts.map((broadcast) => (
          <div
            key={broadcast._id}
            className="announcement-card"
            style={{ borderLeft: `6px solid ${getPriorityColor(broadcast.priority)}` }}
          >
            <div className="card-header">
              <div className="card-title-group">
                <span className="card-icon">
                  {broadcast.priority === 'high' ? '🔴' : '📣'}
                </span>
                <h3 className="card-title">{broadcast.title}</h3>
              </div>
              <div className={`priority-tag ${broadcast.priority === 'medium' ? 'normal' : broadcast.priority}`}>
                {broadcast.priority.toUpperCase()} {broadcast.priority === 'high' ? '▼' : '▼'}
              </div>
            </div>

            <div className="card-meta">
              <span>Sent to: <span className="meta-value">All Employees</span></span>
              <span className="meta-divider">|</span>
              <span>By: <span className="meta-value">{broadcast.createdBy?.firstName} {broadcast.createdBy?.lastName}</span></span>
              <span className="meta-divider">|</span>
              <span>{new Date(broadcast.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
            </div>

            <div className="card-message">
              {broadcast.message}
            </div>

            <div className="card-actions">
              <button
                className="btn-action"
                onClick={() => setExpandedBroadcastId(expandedBroadcastId === broadcast._id ? null : broadcast._id)}
              >
                👁️ View
              </button>
              {(hasPermission('broadcasts', 'edit') || isAdmin()) && (
                <button className="btn-action" onClick={() => handleEdit(broadcast)}>✏️ Edit</button>
              )}
              {(hasPermission('broadcasts', 'delete') || isAdmin()) && (
                <button className="btn-action delete" onClick={() => handleDelete(broadcast._id)}>🗑️ Delete</button>
              )}
              {adminView && (
                <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#94a3b8', alignSelf: 'center' }}>
                  👀 {broadcast.viewCount || 0} views | 💬 {broadcast.totalComments || 0} comments
                </span>
              )}
            </div>

            {/* Expandable Comments Section */}
            {expandedBroadcastId === broadcast._id && (
              <div className="broadcast-comments" style={{ marginTop: '20px', padding: '15px', background: '#f8fafc', borderRadius: '8px' }}>
                <h4 style={{ fontSize: '14px', marginBottom: '10px' }}>Comments ({broadcast.comments?.length || 0})</h4>
                <div style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '10px' }}>
                  {broadcast.comments?.map(comment => (
                    <div key={comment._id} style={{ padding: '8px', borderBottom: '1px solid #edf2f7', fontSize: '13px' }}>
                      <strong>{comment.commentedBy?.firstName}:</strong> {comment.comment}
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Add a comment..."
                    value={commentText[broadcast._id] || ''}
                    onChange={(e) => setCommentText({ ...commentText, [broadcast._id]: e.target.value })}
                    style={{ fontSize: '13px' }}
                  />
                  <button
                    className="btn-new-broadcast"
                    style={{ padding: '4px 12px', fontSize: '12px' }}
                    onClick={() => handleAddComment(broadcast._id)}
                  >
                    Post
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}

        {filteredBroadcasts.length === 0 && (
          <div className="announcement-card" style={{ textAlign: 'center', padding: '60px', color: '#94a3b8' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
            <h3>No announcements found</h3>
            <p>Try adjusting your search or filters.</p>
          </div>
        )}
      </div>

      {/* Create Broadcast Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>New Broadcast</h2>
              <button style={{ border: 'none', background: 'none', fontSize: '24px', cursor: 'pointer' }} onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Message</label>
                  <textarea
                    className="form-control"
                    rows="4"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                  ></textarea>
                </div>
                <div className="form-group">
                  <label>Priority</label>
                  <select
                    className="form-control"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Normal</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-action" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn-new-broadcast">Post Announcement</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Broadcast Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => { setShowEditModal(false); setEditingBroadcast(null); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Broadcast</h2>
              <button style={{ border: 'none', background: 'none', fontSize: '24px', cursor: 'pointer' }} onClick={() => { setShowEditModal(false); setEditingBroadcast(null); }}>×</button>
            </div>
            <form onSubmit={handleUpdate}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Message</label>
                  <textarea
                    className="form-control"
                    rows="4"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                  ></textarea>
                </div>
                <div className="form-group">
                  <label>Priority</label>
                  <select
                    className="form-control"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Normal</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-action" onClick={() => { setShowEditModal(false); setEditingBroadcast(null); }}>Cancel</button>
                <button type="submit" className="btn-new-broadcast">Update Announcement</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Broadcasts;
